import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useRef, useState } from 'react';
import { Rocket, Check, X, AlertCircle } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import { ALL_BUTTONS } from '../../components/pos/PosButtonLayoutTab';
import { resolveAllowedButtonIds } from '../../components/pos/posButtonEntitlements';
import type { DeviceUnitRow } from '../../services/hardwareApi';
import { readerBySerial, TENDER_FOR_CLASS } from './readerMatch';

/**
 * SET UP POS — the coupled pair, carried through to a working till.
 *
 * Coupling answers which reader a till uses. Everything after it up to a till
 * that can take a payment is mechanical, and was four separate steps an
 * operator worked one at a time. This runs them in order, for real:
 *
 *   1. Hand the till over   — mints/returns the owner's POS pin
 *   2. Payment types        — the tender the reader is ENROLLED for, plus
 *                             signature on it and tips at the location
 *   3. Button layout        — the tenant's entitled buttons, then Set up POS
 *
 * and stops. It goes no further deliberately: every stage past this one is
 * either a claim about where a box physically is, or a piece of human proof —
 * a photo of a printed receipt, a reconciliation against Stripe. A runner
 * cannot produce those, and ticking them would put assertions on the order that
 * nobody made.
 *
 * WHY IT IS PACED. Each step holds for MIN_STEP_MS even when the call returns
 * instantly, because the point is that an operator can SEE which step is
 * running and which one failed. A flicker through four states teaches nothing
 * and, when something breaks, leaves them with no idea how far it got.
 */

const MIN_STEP_MS = 6000;

type StepState = 'pending' | 'running' | 'done' | 'failed' | 'skipped';

interface RunStep {
  id: string;
  label: string;
  /** Filled in as it runs: what actually happened, in the runner's own words. */
  detail?: string;
  state: StepState;
}

const BASE_STEPS: RunStep[] = [
  { id: 'handover', label: 'Hand the till over', state: 'pending' },
  { id: 'tenders', label: 'Payment types, signature and tips', state: 'pending' },
  { id: 'layout', label: 'Button layout and Set up POS', state: 'pending' },
];


const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export interface PosSetupRunnerProps {
  tenantId: string;
  locationId?: string;
  locationName?: string;
  /** The owner the handover mints a pin for. Without it, that step is skipped. */
  ownerEmail?: string;
  /** The coupled POS unit this run is for. */
  unit: DeviceUnitRow;
  unitLabel: string;
  /** The coupled READER's serial — how its enrolled EMV record is found, and
   *  the only field the inventory unit and the pool device both carry. */
  coupledSerial?: string;
  onDone: () => void;
}

const PosSetupRunner: React.FC<PosSetupRunnerProps> = ({
  tenantId, locationId, locationName, ownerEmail, unit, unitLabel, coupledSerial, onDone,
}) => {
  const [steps, setSteps] = useState<RunStep[] | null>(null);
  const [running, setRunning] = useState(false);
  const [fatal, setFatal] = useState<string | null>(null);
  const runId = useRef(0);

  const patch = (id: string, next: Partial<RunStep>) =>
    setSteps((cur) => (cur || []).map((s) => (s.id === id ? { ...s, ...next } : s)));

  /**
   * One step: mark it running, do the work, hold the floor for MIN_STEP_MS,
   * then settle it green or red. A thrown error settles red and STOPS the run —
   * every step here depends on the one before it, so carrying on would just
   * produce a second failure with a less useful message.
   */
  const step = async (id: string, work: () => Promise<string>): Promise<boolean> => {
    const mine = runId.current;
    patch(id, { state: 'running', detail: undefined });
    const started = Date.now();
    try {
      const detail = await work();
      await sleep(Math.max(0, MIN_STEP_MS - (Date.now() - started)));
      if (runId.current !== mine) return false; // a restart overtook this run
      patch(id, { state: 'done', detail });
      return true;
    } catch (e: any) {
      await sleep(Math.max(0, MIN_STEP_MS - (Date.now() - started)));
      if (runId.current !== mine) return false;
      patch(id, { state: 'failed', detail: e?.serverMessage || e?.message || 'failed' });
      return false;
    }
  };

  const start = async () => {
    /* A SECOND PRESS THROWS THE FIRST RUN AWAY. The list collapses to pending
       and everything starts again — which is what pressing it again means. Any
       step still in flight from the previous run checks `runId` before it
       writes, so a slow call from the old run can never land on the new one. */
    runId.current += 1;
    const mine = runId.current;
    setFatal(null);
    setSteps(BASE_STEPS.map((s) => ({ ...s })));
    setRunning(true);

    try {
      // ── The register this run acts on. Everything below needs it. ──────────
      let terminal: any;
      try {
        const res: any = await api.posTerminals.list(tenantId);
        const list: any[] = res?.terminals || [];
        terminal = list.find((t) => t.deviceUnitId === unit.unitId)
          || list.find((t) => !t.deviceUnitId && String(t.registerName || '').includes(unitLabel));
      } catch (e: any) {
        setFatal(e?.message || 'Could not read this tenant’s registers.');
        setRunning(false);
        return;
      }
      if (!terminal) {
        setFatal('This device has no register yet — create it on the Create register step, then run this.');
        setRunning(false);
        return;
      }

      // ── 1. HAND THE TILL OVER ─────────────────────────────────────────────
      if (!ownerEmail) {
        patch('handover', { state: 'skipped', detail: 'no owner email on this tenant — do it on the Hand over step' });
      } else {
        const ok = await step('handover', async () => {
          const r: any = await api.posAuth.handover(tenantId, { ownerEmail, locationId });
          /* NEVER the pin itself. It is shown on the handover step, which is
             where somebody is deliberately looking at it — a runner's log is
             read over shoulders and screenshotted into tickets. */
          return r?.pinReused ? 'owner pin already issued — reused' : 'owner pin issued and emailed';
        });
        if (!ok || runId.current !== mine) { setRunning(false); return; }
      }

      // ── 2. PAYMENT TYPES, SIGNATURE, TIPS ─────────────────────────────────
      const ok2 = await step('tenders', async () => {
        /* The tender the READER IS ENROLLED FOR, not a guess from the catalog:
           a P17 is the same box on USB or cloud, and the service refuses the
           one it was not enrolled for. */
        let tender: string | undefined;
        /* THE DEVICE ID TRAVELS WITH THE TENDER, ALWAYS.

           Turning a card tender on and BINDING the reader are one save, not
           two: the service refuses a device-backed tender that arrives without
           an `emvDeviceId` (EMV_DEVICE_REQUIRED), because a till that accepts
           cards with no reader bound is exactly the half-configured state the
           interlock exists to prevent. The runner already resolves the pool
           record to read its enrolled lane — it must carry the id through as
           well, or it asks for the state it is not allowed to have. Sending it
           on every run is also what makes "Set up again" a rebind rather than
           a refusal when the reader has been swapped. */
        let emvDeviceId: string | undefined;
        if (locationId) {
          const devices: any = await api.posNmi.listEmvDevicesAtLocation(tenantId, locationId);
          const list: any[] = devices?.devices || devices?.items || (Array.isArray(devices) ? devices : []);
          /* SERIAL ONLY. "The only reader in the pool" used to stand in when
             the serial did not match — and with dead readers and duplicate
             serials in pools, that bound tills to the wrong reader. */
          const match = readerBySerial(list, coupledSerial);
          if (!match.device) throw new Error(match.problem);
          const rec = match.device;
          tender = TENDER_FOR_CLASS[String(rec.deviceClass || '')];
          emvDeviceId = rec.deviceId;
          if (!tender) {
            throw new Error(`the reader with serial ${String(coupledSerial || '').trim()} is registered as "${rec.deviceClass || 'unknown'}", which has no card tender this runner can enable`);
          }
        }
        if (!tender || !emvDeviceId) {
          throw new Error('the coupled reader is not registered at this location yet, so there is no card tender to enable — register it on the card processing step');
        }

        const existing: any = await api.posNmi.getPaymentTypes(tenantId, terminal.terminalId).catch(() => ({}));
        const now: string[] = existing?.assigned || existing?.enabled || [];
        const next = Array.from(new Set([...now, tender]));
        /* The merchant's enabled list gains the tender being turned on and is
           otherwise left as it is — never reset to the assigned list. */
        const enabledNow: string[] = Array.isArray(existing?.enabled) ? existing.enabled : now;
        const nextEnabled = Array.from(new Set([...enabledNow, tender]));

        await api.posNmi.putPaymentTypes(tenantId, terminal.terminalId, {
          assigned: next,
          enabled: nextEnabled,
          emvDeviceId,
          // Only the key this step changes — the handler merges per key.
          signatureRequired: { [tender]: true },
        });

        if (locationId) {
          await api.posNmi.putPaymentGateway(tenantId, locationId, { tipsEnabled: true });
        }
        const name = tender === 'credit_dejavoo_cloud' ? 'Card — Dejavoo (SPIn Cloud)'
          : tender === 'credit_dejavoo_rtpay' ? 'Card — RubyTill Pay' : 'Card — Dejavoo (SPIn USB)';
        return `${name} on, signature required, tips granted${locationName ? ` at ${locationName}` : ''}`;
      });
      if (!ok2 || runId.current !== mine) { setRunning(false); return; }

      // ── 3. BUTTON LAYOUT, THEN SET UP POS ─────────────────────────────────
      await step('layout', async () => {
        const reg: any = await api.posConfig.getRegister(terminal.terminalId, tenantId).catch(() => null);
        const layout = reg?.register?.buttonLayout || reg?.buttonLayout || {};
        const already: string[] = Array.isArray(layout.available) ? layout.available : [];

        let available = already;
        if (available.length === 0) {
          available = await resolveAllowedButtonIds(tenantId, ALL_BUTTONS);
          if (available.length === 0) {
            throw new Error('this tenant has no POS buttons in its packages, so there is nothing to lay out');
          }
          /* The whole buttonLayout object is REPLACED by this endpoint, so
             keypadSlots and buttonCustomizations are sent back exactly as read.
             Omitting them wipes the per-terminal work done in the platform-app
             Keypad Designer — the same bug this write has caused before. */
          await api.posConfig.updateRegister(terminal.terminalId, {
            tenantId,
            buttonLayout: {
              available,
              pinned: (layout.pinned || []).filter((id: string) => available.includes(id)),
              keypadSlots: layout.keypadSlots,
              buttonCustomizations: layout.buttonCustomizations,
            },
          } as any);
        }

        const res: any = await api.posConfig.setupRegister(terminal.terminalId, tenantId);
        const installed = (res?.steps || []).length;
        return `${available.length} buttons enabled · ${installed} default${installed === 1 ? '' : 's'} installed`;
      });
    } finally {
      if (runId.current === mine) {
        setRunning(false);
        onDone();
      }
    }
  };

  const allDone = !!steps && steps.every((s) => s.state === 'done' || s.state === 'skipped');

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-[12.5px] font-bold text-stone-800">Set up this till</h4>
          <p className="mt-0.5 text-[11.5px] leading-relaxed text-stone-500">
            Hands the till over, switches on the tender this reader is enrolled for with signature and
            tips, installs the button layout — then stops. Everything after that is a photo or a
            reconciliation somebody has to do.
          </p>
        </div>
        <button
          type="button"
          onClick={start}
          disabled={running}
          className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-ruby px-4 text-sm font-semibold text-white transition-colors hover:bg-ruby-deep disabled:cursor-not-allowed disabled:opacity-40"
        >
          {running ? <RubyGemMark size={15} drawing color="currentColor" /> : <Rocket size={15} />}
          {running ? 'Setting up…' : steps ? 'Set up again' : 'Set up POS'}
        </button>
      </div>

      {fatal && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
          <AlertCircle size={14} className="mt-0.5 shrink-0" /> {fatal}
        </div>
      )}

      {steps && (
        <ol className="mt-3 space-y-1.5">
          {steps.map((s) => (
            <li
              key={s.id}
              className={`flex items-start gap-2.5 rounded-lg px-2.5 py-2 transition-all duration-500 ${
                s.state === 'running' ? 'bg-ruby-tint/70'
                  : s.state === 'done' ? 'bg-emerald-50'
                    : s.state === 'failed' ? 'bg-red-50'
                      : 'bg-stone-50'
              }`}
            >
              <span className="mt-0.5 shrink-0">
                {s.state === 'running' && <RubyGemMark size={14} drawing />}
                {s.state === 'done' && <Check size={14} className="text-emerald-600" />}
                {s.state === 'failed' && <X size={14} className="text-red-600" />}
                {(s.state === 'pending' || s.state === 'skipped') && (
                  <span className="block h-3.5 w-3.5 rounded-full border border-stone-300" />
                )}
              </span>
              <div
                /* A step nobody has reached yet is dimmed and blurred, so the
                   one that IS running is the only thing legible. */
                className={`min-w-0 flex-1 transition-all duration-500 ${
                  s.state === 'pending' ? 'opacity-40 blur-[1.5px]' : 'opacity-100 blur-0'
                }`}
              >
                <div className={`text-[12px] font-semibold ${
                  s.state === 'failed' ? 'text-red-700'
                    : s.state === 'done' ? 'text-emerald-800'
                      : 'text-stone-700'
                }`}
                >
                  {s.label}
                </div>
                {s.detail && (
                  <div className={`text-[11px] leading-snug ${s.state === 'failed' ? 'text-red-600' : 'text-stone-500'}`}>
                    {s.detail}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}

      {allDone && !running && (
        <p className="mt-2 text-[11.5px] font-semibold text-emerald-700">
          {unitLabel} is set up. The remaining steps need a test sale and its receipt.
        </p>
      )}
    </div>
  );
};

export default PosSetupRunner;
